import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, ChevronLeft } from "lucide-react";

const PreviewDialog = ({ pack, isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  
  // Reset to first page when pack changes
  useEffect(() => {
    setCurrentPage(0);
  }, [pack?.id]);

  const getPageCount = (packId) => {
    const pageCounts = {
      noah_ark: 15,
      david_goliath: 15,
      moses: 13,
      color_by_number: 12,
      jesus_feeds_5000: 13,
      adam_eve: 14
    };
    return pageCounts[packId] || 12;
  };

  const getPreviewImage = (packId, pageNum) => {
    return `/previews/${packId}/page_${pageNum + 1}.png`;
  };

  const handlePrevious = () => {
    const totalPages = getPageCount(pack?.id);
    setCurrentPage((prev) => (prev === 0 ? totalPages - 1 : prev - 1));
  };

  const handleNext = () => {
    const totalPages = getPageCount(pack?.id);
    setCurrentPage((prev) => (prev === totalPages - 1 ? 0 : prev + 1));
  };

  if (!pack) return null;

  const totalPages = getPageCount(pack.id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{pack.title}</DialogTitle>
          <DialogDescription>{pack.description}</DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Image Preview with Navigation */}
            <div className="relative">
              <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-white">
                <img 
                  src={getPreviewImage(pack.id, currentPage)} 
                  alt={`${pack.title} - Page ${currentPage + 1}`}
                  className="w-full h-auto"
                  data-testid="preview-image"
                />
              </div>
              
              {/* Navigation Arrows */}
              <button
                onClick={handlePrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-emerald-900/90 hover:bg-emerald-800 text-white rounded-full flex items-center justify-center shadow-lg transition-all"
                data-testid="preview-prev-btn"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-emerald-900/90 hover:bg-emerald-800 text-white rounded-full flex items-center justify-center shadow-lg transition-all"
                data-testid="preview-next-btn"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              
              {/* Page Counter */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-1.5 rounded-full text-sm font-medium">
                Page {currentPage + 1} of {totalPages}
              </div>
            </div>
            
            {/* Info Panel */}
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h5 className="font-semibold text-emerald-900 mb-2">What's Included:</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>- {pack.pages} high-quality coloring pages</li>
                  <li>- Instant digital download (ZIP file)</li>
                  <li>- Printable on standard letter paper</li>
                  <li>- Beautiful detailed illustrations</li>
                  <li>- Perfect for all ages</li>
                </ul>
              </div>
              
              <div className="bg-amber-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-amber-700">${pack.price.toFixed(2)}</span>
                  <Badge className="bg-emerald-900 text-amber-500">Instant Download</Badge>
                </div>
              </div>
              
              {/* Page Thumbnails */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h5 className="font-semibold text-emerald-900 mb-3 text-sm">Browse All Pages:</h5>
                <div className="grid grid-cols-6 gap-2">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`aspect-square rounded border-2 overflow-hidden transition-all ${
                        currentPage === i 
                          ? 'border-emerald-500 ring-2 ring-emerald-200' 
                          : 'border-slate-200 hover:border-amber-400'
                      }`}
                      data-testid={`preview-thumb-${i + 1}`}
                    >
                      <img 
                        src={getPreviewImage(pack.id, i)} 
                        alt={`Page ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewDialog;
